// The single source of truth for Rumaal Rank scoring.
//
// The scoring grid, the parent app, the certificates and every report must all
// agree on what a score means. The previous Google Sheets system duplicated this
// arithmetic across fragile per-cell formulas, which is exactly how a student's
// certificate ended up disagreeing with their result. Nothing anywhere may
// recompute a total, a percentage or a pass status — import from here.
//
// Mirrored on the frontend at src/utils/scoring.js. Change both together.

// The four rubric categories, in the order they are always displayed.
// `key` is the CeremonyResult field; `label` is what a human ever sees.
// Ordered as the academy's own certificate prints them, so the scoring grid
// and the report card read the same way round. Entering scores at speed with
// parents watching is exactly when a transposed column would go unnoticed.
export const RUBRIC_CATEGORIES = [
  { key: 'smile', label: 'Smile', short: 'SMILE' },
  { key: 'energy', label: 'Energy', short: 'ENERGY' },
  { key: 'execution', label: 'Execution', short: 'EXEC' },
  // Amrit's "Energy & Execution" in the original brief was a typo; the fourth
  // category is Grace/Nakhra — expressive flair, a real technical criterion.
  { key: 'nakhra', label: 'Grace / Nakhra', short: 'NAKHRA' },
];

export const CATEGORY_KEYS = RUBRIC_CATEGORIES.map((c) => c.key);

export const MAX_PER_CATEGORY = 10;
export const MAX_TOTAL = RUBRIC_CATEGORIES.length * MAX_PER_CATEGORY; // 40
export const PASS_PERCENTAGE = 75;
// 75% of 40. Stated both ways in the client's own words ("75% — that is 30 out
// of 40"), so both are kept and asserted equal below.
export const PASS_TOTAL = (MAX_TOTAL * PASS_PERCENTAGE) / 100; // 30

// Scores are entered in half-points: 6.5 and 7.5 are real values in production
// data. Anything finer is a typo, and integers-only would silently destroy
// judgements the admin actually made.
export const SCORE_STEP = 0.5;

// Status codes. `not_attended` is deliberately distinct from a zero: a student
// who did not show up has no result, and must never be averaged as 0.
export const STATUS = {
  PASS: 'pass',
  IN_PROGRESS: 'in_progress',
  NOT_ATTENDED: 'not_attended',
  INCOMPLETE: 'incomplete',
};

// ⛔ The word "FAIL" is banned by the client, in the UI, on certificates, in
// emails and in exports. These are children, and the status prints on something
// a parent shows their family. Below the threshold is always "IN PROGRESS" —
// retesting next month is normal and expected.
const STATUS_LABELS = {
  [STATUS.PASS]: 'PASS',
  [STATUS.IN_PROGRESS]: 'IN PROGRESS',
  [STATUS.NOT_ATTENDED]: 'Did not attend',
  [STATUS.INCOMPLETE]: null, // still being scored — show nothing rather than guess
};

/**
 * Is this a score an admin is allowed to enter? 0–10 in 0.5 steps.
 * The scoring grid rejects anything else as it is typed.
 */
export function isValidScore(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  if (value < 0 || value > MAX_PER_CATEGORY) return false;
  // Multiply before comparing: 7.5 / 0.5 is exact in binary floating point, but
  // guard the general case anyway rather than trusting a modulo on floats.
  return Number.isInteger(Math.round(value / SCORE_STEP)) &&
    Math.abs(value / SCORE_STEP - Math.round(value / SCORE_STEP)) < 1e-9;
}

/**
 * Round to a whole percent, half up. 33.5/40 = 83.75% -> 84%.
 * Display only — the pass decision never uses the rounded value.
 */
export function roundPercentage(exact) {
  return Math.round(exact);
}

/**
 * Compute everything derived about one student's ceremony result.
 *
 * Accepts a partially-filled result: the grid shows a running total while the
 * admin is still typing, but withholds the percentage and the verdict until all
 * four categories are in, so nobody reads a half-entered row as a failure.
 *
 * @param {object} result
 * @param {boolean} [result.attended=true]
 * @param {number|null} [result.smile]
 * @param {number|null} [result.execution]
 * @param {number|null} [result.energy]
 * @param {number|null} [result.nakhra]
 * @returns {{
 *   attended: boolean,
 *   complete: boolean,
 *   scored: number,
 *   total: number|null,
 *   percentage: number|null,
 *   percentageExact: number|null,
 *   status: string,
 *   statusLabel: string|null,
 *   passed: boolean,
 * }}
 */
export function scoreResult(result = {}) {
  const attended = result.attended !== false;

  // Did-not-attend is a state, not a score. No total, no percentage, no verdict
  // — and nothing that could drag an average down later.
  if (!attended) {
    return {
      attended: false,
      complete: false,
      scored: 0,
      total: null,
      percentage: null,
      percentageExact: null,
      status: STATUS.NOT_ATTENDED,
      statusLabel: STATUS_LABELS[STATUS.NOT_ATTENDED],
      passed: false,
    };
  }

  const values = CATEGORY_KEYS.map((key) => {
    const value = result[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  });

  const entered = values.filter((v) => v !== null);
  const complete = entered.length === CATEGORY_KEYS.length;
  const total = entered.reduce((sum, v) => sum + v, 0);

  if (!complete) {
    return {
      attended: true,
      complete: false,
      scored: entered.length,
      // The running total is genuinely useful while scoring, so return it — but
      // the caller must render it as provisional (the grid greys it).
      total: entered.length > 0 ? round1(total) : null,
      percentage: null,
      percentageExact: null,
      status: STATUS.INCOMPLETE,
      statusLabel: STATUS_LABELS[STATUS.INCOMPLETE],
      passed: false,
    };
  }

  const percentageExact = (total / MAX_TOTAL) * 100;
  // Compare the exact percentage, never the rounded one: 29.5/40 is 73.75%,
  // which displays as 74% but is not a pass. With a 40-point rubric this is
  // identical to total >= 30.
  const passed = percentageExact >= PASS_PERCENTAGE;
  const status = passed ? STATUS.PASS : STATUS.IN_PROGRESS;

  return {
    attended: true,
    complete: true,
    scored: entered.length,
    total: round1(total),
    percentage: roundPercentage(percentageExact),
    percentageExact,
    status,
    statusLabel: STATUS_LABELS[status],
    passed,
  };
}

/**
 * The label a human sees for a status code. Never returns "FAIL".
 */
export function statusLabel(status) {
  return STATUS_LABELS[status] ?? null;
}

/**
 * Average a set of results per category, for the parent app's "focus area" and
 * the instructor performance view.
 *
 * Only complete, attended results count — a student who missed a ceremony must
 * not be averaged as a zero, and a half-scored row is not a judgement yet.
 *
 * @param {Array<object>} results
 * @returns {{ counted: number, averages: Record<string, number|null>, weakest: string|null }}
 */
export function averageByCategory(results = []) {
  const usable = results.filter((r) => scoreResult(r).complete);
  const averages = {};

  CATEGORY_KEYS.forEach((key) => {
    if (usable.length === 0) {
      averages[key] = null;
      return;
    }
    const sum = usable.reduce((acc, r) => acc + r[key], 0);
    averages[key] = round1(sum / usable.length);
  });

  // The lowest category is what a parent is told to work on and what the
  // instructor view flags, so ties resolve by rubric order for stability.
  let weakest = null;
  if (usable.length > 0) {
    weakest = CATEGORY_KEYS.reduce(
      (low, key) => (averages[key] < averages[low] ? key : low),
      CATEGORY_KEYS[0]
    );
  }

  return { counted: usable.length, averages, weakest };
}

/**
 * Pass rate across a set of results, for the instructor performance view.
 * Absent and incomplete results are excluded from both numerator and
 * denominator — an instructor is not judged on who turned up.
 */
export function passRate(results = []) {
  const scored = results.map(scoreResult).filter((s) => s.complete);
  if (scored.length === 0) return { tested: 0, passed: 0, rate: null };
  const passed = scored.filter((s) => s.passed).length;
  return {
    tested: scored.length,
    passed,
    rate: Math.round((passed / scored.length) * 100),
  };
}

// Totals are sums of halves, so one decimal is exact. Guards against float
// artefacts like 33.499999999999996 reaching a certificate.
function round1(value) {
  return Math.round(value * 10) / 10;
}
